import { useEffect, useState, type ReactNode } from 'react';
import { AuthProvider, useAuth } from '@/features/auth/AuthProvider';
import { QueryProvider } from '@/core/datos/QueryProvider';
import { ColaProvider } from '@/core/offline/ColaProvider';
import { BundleProvider } from '@/core/offline/BundleProvider';
import { SenalGlobal } from '@/core/offline/SenalGlobal';
import { ActualizacionDisponible } from '@/core/pwa/ActualizacionDisponible';
import { BannerSuscripcion } from '@/features/billing/BannerSuscripcion';
import { LoginPage } from '@/features/auth/LoginPage';
import { OrgProvider, useOrg } from '@/features/team/OrgProvider';
import { Bienvenida } from '@/features/team/Bienvenida';
import { OrgSwitcher } from '@/features/team/OrgSwitcher';
import { ClientsPage } from '@/features/clients/ClientsPage';
import { ConfigPage } from '@/features/catalog/ConfigPage';
import { HonorariosPage } from '@/features/catalog/HonorariosPage';
import { PreciosPage } from '@/features/catalog/PreciosPage';
import { WorksPage } from '@/features/works/WorksPage';
import { WorkDetailPage } from '@/features/works/WorkDetailPage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { HomePage, type DestinoInicio } from '@/features/home/HomePage';
import { MasPage } from '@/features/home/MasPage';
import { MiCuentaPage } from '@/features/auth/MiCuentaPage';
import { ConfirmarAccion } from '@/ui/ConfirmarAccion';
import { TeamPage } from '@/features/team/TeamPage';
import { ClosingPage } from '@/features/closing/ClosingPage';
import { ReportsPage } from '@/features/reports/ReportsPage';
import { PeriodProvider } from '@/features/period/PeriodProvider';
import { APP_NAME } from './constants';
import { BandaEntorno, hayBandaDeEntorno } from './BandaEntorno';
import { conectarBotonAtras, useBotonAtras } from '@/core/android/atras';
import { aplicarInsetsDeAndroid } from '@/core/android/insets';

/**
 * A dónde se puede estar parado.
 *
 * Cada destino lleva sus datos adentro en vez de guardarlos en variables
 * sueltas al costado. Es lo que permite que volver *restituya* la pantalla
 * anterior tal como estaba —con su filtro puesto, con su trabajo abierto— en
 * lugar de devolver una versión en blanco.
 */
type Destino =
  | { p: 'inicio' }
  | { p: 'trabajos'; abrirNuevo?: boolean }
  | { p: 'detalle'; workId: string }
  | { p: 'clientes' }
  | { p: 'resumen' }
  | { p: 'liquidacion' }
  | { p: 'reportes' }
  | { p: 'honorarios' }
  | { p: 'precios' }
  | { p: 'equipo' }
  | { p: 'config' }
  | { p: 'cuenta' }
  | { p: 'mas' };

const INICIO: Destino[] = [{ p: 'inicio' }];

/**
 * La navegación, después de usar la aplicación en el campo.
 *
 * **No hay barra inferior.** La tuvo, y repetía lo que ya estaba en Inicio: dos
 * caminos a las mismas cuatro pantallas, ocupando una franja permanente de una
 * pantalla que nunca sobra. El modelo quedó en uno solo y se explica en una
 * frase: *Inicio es el centro, se entra a una sección, se vuelve.*
 *
 * Lo que reemplaza a la barra es una **pila de verdad**. Importa porque el
 * botón Atrás de Android no perdona: con una sola variable de «pantalla
 * actual», volver desde el detalle de un trabajo tenía que adivinar de dónde se
 * había venido, y adivinaba mal —devolvía a Inicio y hacía perder el filtro—.
 * Con la pila, Atrás deshace exactamente el último paso, y desde Inicio —y sólo
 * desde ahí— cierra la aplicación.
 *
 * Un formulario con datos cargados se defiende solo: registra su propia guardia
 * y la atiende antes que ésta, así Atrás pregunta en lugar de tirar la visita.
 */
function Shell() {
  const { session, loading, signOut } = useAuth();
  /**
   * Cerrar sesión se pregunta antes.
   *
   * No por ceremonia: **destruye la credencial que permite abrir la aplicación
   * sin señal**, y eso es correcto —quien se va, se va— pero es invisible desde
   * el botón. Alguien que lo aprieta para «cerrar la aplicación» descubre lo
   * que hizo recién al día siguiente, en el campo y sin señal para volver a
   * entrar.
   */
  const [preguntandoSalir, setPreguntandoSalir] = useState(false);
  const [pila, setPila] = useState<Destino[]>(INICIO);

  const actual = pila[pila.length - 1] ?? { p: 'inicio' };
  const ir = (d: Destino) => setPila((prev) => [...prev, d]);
  const volver = () => setPila((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));

  // Ir a Trabajos es ir al mes que ya se está mirando: el período es
  // compartido y es el único eje del listado. No se lleva ningún filtro más
  // —no hay dónde ponerlo ni cómo saber que está puesto.
  const abrirTrabajos = () => ir({ p: 'trabajos' });

  const irDesdeInicio = (destino: DestinoInicio | 'reportes' | 'equipo' | 'cuenta' | 'mas') => {
    if (destino === 'nuevo-trabajo') return ir({ p: 'trabajos', abrirNuevo: true });
    ir({ p: destino } as Destino);
  };

  // Atrás deshace el último paso. En Inicio no hay nada que deshacer y se
  // devuelve `false`, que es lo que deja salir de la aplicación.
  useBotonAtras(() => {
    if (pila.length <= 1) return false;
    volver();
    return true;
  });

  if (loading) return <p className="p-6 text-sm text-[var(--tinta-suave)]">Cargando…</p>;

  // Sin sesión no hay app. RLS igual bloquea los datos, pero no tiene sentido
  // mostrar una interfaz vacía.
  if (!session) return <LoginPage />;

  return (
    <OrgProvider>
      <SinVeterinaria>
        <ColaDeLaOrganizacion>
          <PeriodProvider>
            <div className="margen-lateral-seguro zona-inferior mx-auto flex min-h-full max-w-2xl flex-col">
              <BandaEntorno />
              {/* Sin la franja de staging delante, es el encabezado el que tiene
                que llegar hasta el borde. Sólo uno de los dos lo hace: el inset
                no se consume, así que marcarlos a los dos duplicaría el
                relleno. */}
              {/* El marco: mínimo, y sin línea debajo. Antes tenía el nombre de
                la aplicación en negrita, y en Inicio quedaba dos veces —una en
                el marco y otra como título de la pantalla—. Acá el nombre es
                el botón para volver al tablero, así que en el tablero mismo no
                hace falta. */}
              <header
                className={`flex items-center justify-between gap-3 px-4 py-2 ${
                  hayBandaDeEntorno() ? '' : 'zona-superior'
                }`}
              >
                {actual.p === 'inicio' ? (
                  <span className="min-w-0" />
                ) : (
                  <button
                    type="button"
                    onClick={() => setPila(INICIO)}
                    className="min-h-11 min-w-0 truncate text-left text-sm text-[var(--tinta-suave)]"
                    data-testid="ir-a-inicio"
                  >
                    {APP_NAME}
                  </button>
                )}
                <div className="flex items-center gap-3">
                  <OrgSwitcher />
                  {/* Dice «Cerrar sesión» y no «Salir».
                      «Salir» se lee como «cerrar la aplicación» —es lo que
                      significa en castellano— y lo que hace es otra cosa: te
                      deja afuera hasta que consigas señal. Un botón que no
                      nombra lo que hace, al lado del nombre de la aplicación,
                      es una trampa. */}
                  <button
                    onClick={() => setPreguntandoSalir(true)}
                    className="min-h-11 text-sm text-[var(--tinta-suave)]"
                    data-testid="signout"
                  >
                    Cerrar sesión
                  </button>
                </div>
              </header>

              {/* Ni el estado del plan ni el de la conexión son de una pantalla:
                son de la aplicación entera. Van debajo del encabezado y arriba
                de todo lo demás. */}
              {preguntandoSalir && (
                <ConfirmarAccion
                  titulo="Cerrar sesión"
                  pregunta="¿Cerrás la sesión en este teléfono?"
                  detalle="Para volver a entrar vas a necesitar señal. Lo que tengas sin sincronizar se guarda igual y se envía cuando vuelvas."
                  confirmar="Cerrar sesión"
                  onConfirmar={() => {
                    setPreguntandoSalir(false);
                    void signOut();
                  }}
                  onCancelar={() => setPreguntandoSalir(false)}
                  testId="confirmar-salir"
                />
              )}

              <SenalGlobal />
              <BannerSuscripcion />

              {actual.p === 'inicio' && <HomePage onIr={irDesdeInicio} />}

              {/* Estas tres no traen su propio «Volver» —eran pestañas— así que
                se lo pone el marco, en el mismo lugar donde lo dibujan las que
                sí lo tienen. */}
              {actual.p === 'trabajos' && (
                <ConVolver onVolver={volver}>
                  <WorksPage
                    abrirNuevo={actual.abrirNuevo ?? false}
                    onVerDetalle={(id) => ir({ p: 'detalle', workId: id })}
                  />
                </ConVolver>
              )}
              {actual.p === 'clientes' && (
                <ConVolver onVolver={volver}>
                  <ClientsPage />
                </ConVolver>
              )}
              {actual.p === 'mas' && (
                <ConVolver onVolver={volver}>
                  <MasPage onIr={irDesdeInicio} />
                </ConVolver>
              )}
              {actual.p === 'cuenta' && <MiCuentaPage onVolver={volver} />}

              {actual.p === 'detalle' && (
                <WorkDetailPage
                  workId={actual.workId}
                  onVolver={volver}
                  // Editar sale del detalle y entra al listado con el formulario
                  // abierto; volver desde ahí devuelve al detalle, que es de
                  // donde se salió.
                  onEditar={() => volver()}
                />
              )}

              {actual.p === 'resumen' && (
                <DashboardPage
                  onVerTrabajos={abrirTrabajos}
                  onIrAConfig={() => ir({ p: 'config' })}
                  onVerLiquidacion={() => ir({ p: 'liquidacion' })}
                  onVerReportes={() => ir({ p: 'reportes' })}
                  onVerTrabajo={(workId) => ir({ p: 'detalle', workId })}
                  onVolver={volver}
                />
              )}
              {actual.p === 'liquidacion' && (
                <ClosingPage onVolver={volver} onVerTrabajos={abrirTrabajos} />
              )}
              {actual.p === 'reportes' && <ReportsPage onVolver={volver} />}
              {actual.p === 'honorarios' && <HonorariosPage onVolver={volver} />}
              {actual.p === 'precios' && <PreciosPage onVolver={volver} />}
              {actual.p === 'equipo' && (
                <ConVolver onVolver={volver}>
                  <TeamPage />
                </ConVolver>
              )}
              {actual.p === 'config' && (
                <ConVolver onVolver={volver}>
                  <ConfigPage />
                </ConVolver>
              )}
            </div>
          </PeriodProvider>
        </ColaDeLaOrganizacion>
      </SinVeterinaria>
    </OrgProvider>
  );
}

/**
 * Quien no pertenece a ninguna veterinaria todavía no puede usar la aplicación.
 *
 * Y hasta acá tampoco se le decía: el tablero se dibujaba con sus seis botones
 * y cada uno llevaba a una pantalla en blanco, porque todas empiezan con
 * `if (!activeOrg) return null`. Alguien que se registraba llegaba exactamente
 * ahí —a una aplicación que parece rota—, que es la primera impresión de
 * quien acaba de instalarla.
 *
 * Ahora ese estado tiene su pantalla: el asistente de arranque. No es un caso
 * de borde, es el primer día de todos los que vengan.
 */
function SinVeterinaria({ children }: { children: ReactNode }) {
  const { organizations, loading, refresh } = useOrg();

  // Mientras se está averiguando no se decide nada: mostrar el asistente a
  // alguien que SÍ tiene veterinaria, aunque sea un instante, lo invitaría a
  // crear una segunda sin querer.
  if (loading) return <p className="p-6 text-sm text-[var(--tinta-suave)]">Cargando…</p>;

  if (organizations.length === 0) {
    return <Bienvenida onListo={() => void refresh()} />;
  }

  return <>{children}</>;
}

/** El «Volver» de las pantallas que no traen uno propio. */
function ConVolver({ children, onVolver }: { children: ReactNode; onVolver: () => void }) {
  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={onVolver}
        className="min-h-11 self-start px-4 pt-3 text-sm underline opacity-70"
        data-testid="volver"
      >
        ‹ Volver
      </button>
      {children}
    </div>
  );
}

/**
 * La cola de altas es de una organización, no del dispositivo: quien trabaja en
 * dos veterinarias no puede ver los borradores de una mientras opera la otra.
 * Va acá adentro porque recién dentro de `OrgProvider` se sabe cuál está
 * activa.
 */
function ColaDeLaOrganizacion({ children }: { children: ReactNode }) {
  const { activeOrg } = useOrg();
  const orgId = activeOrg?.organization_id ?? null;
  // La copia operativa va del mismo lado que la cola y por el mismo motivo: las
  // dos son de UNA organización, y quien tiene dos veterinarias no puede ver
  // los clientes de una mientras opera la otra.
  return (
    <ColaProvider organizationId={orgId}>
      <BundleProvider organizationId={orgId}>{children}</BundleProvider>
    </ColaProvider>
  );
}

export function App() {
  useEffect(() => {
    // Las dos sólo hacen algo dentro de Capacitor. En la web ni el evento de
    // Atrás ni el puente de zonas seguras existen, y las dos se van en silencio.
    // La escucha del estado de red se enciende en `main.tsx`, antes de que se
    // dibuje nada: para cuando esto corre, la primera petición ya salió.
    void conectarBotonAtras();
    aplicarInsetsDeAndroid();
  }, []);

  return (
    <AuthProvider>
      <QueryProvider>
        <ActualizacionDisponible />
        <Shell />
      </QueryProvider>
    </AuthProvider>
  );
}
